import { BaseAdapter } from './base.adapter';
import { DEFAULT_AUTOMATION_GUARDS } from '@/lib/automation-guards';
import type {
  BrandSettings,
  BrandSettingsResponseDto,
  UpdateAutomationThresholdDto,
  UpdateBrandSettingsDto
} from '@/types';

export class BrandAdapter extends BaseAdapter {
  static fromSettingsResponse(dto: BrandSettingsResponseDto): BrandSettings {
    return {
      refreshThreshold: dto.refreshThreshold,
      automationThreshold: dto.automationThreshold,
      qualityJudgeMode: dto.qualityJudgeMode ?? 'strict',
      aiCautionEnabled: dto.aiCautionEnabled ?? false,
      aiCautionMessage: dto.aiCautionMessage ?? 'This response was generated by AI.',
      messageStripPatterns: dto.messageStripPatterns ?? [],
      helpdeskProvider: dto.helpdeskProvider ?? null,
      // Merged over the defaults so a field the server does not send yet reads as today's behaviour.
      automationGuards: {
        ...DEFAULT_AUTOMATION_GUARDS,
        ...dto.automationGuards,
        holdMessage: { ...DEFAULT_AUTOMATION_GUARDS.holdMessage, ...dto.automationGuards?.holdMessage },
        emailReplyDelay: { ...DEFAULT_AUTOMATION_GUARDS.emailReplyDelay, ...dto.automationGuards?.emailReplyDelay }
      }
    };
  }

  static toUpdateSettingsRequest(data: UpdateBrandSettingsDto): UpdateBrandSettingsDto {
    // NOTE: messageStripPatterns is intentionally passed through without sanitization —
    // patterns are matched as plain substrings, so user-pasted text must be preserved exactly.
    const sanitized: UpdateBrandSettingsDto = this.sanitizePayload({
      refreshThreshold: data.refreshThreshold,
      ...(data.qualityJudgeMode !== undefined && { qualityJudgeMode: data.qualityJudgeMode }),
      ...(data.aiCautionEnabled !== undefined && { aiCautionEnabled: data.aiCautionEnabled }),
      ...(data.aiCautionMessage !== undefined && { aiCautionMessage: data.aiCautionMessage })
    });
    if (data.messageStripPatterns !== undefined) {
      sanitized.messageStripPatterns = data.messageStripPatterns;
    }
    // Set directly: sanitizePayload drops null, and null is a valid "unset" here.
    if (data.helpdeskProvider !== undefined) {
      sanitized.helpdeskProvider = data.helpdeskProvider;
    }
    return sanitized;
  }

  static toUpdateAutomationThresholdRequest(
    data: UpdateAutomationThresholdDto
  ): UpdateAutomationThresholdDto {
    return this.sanitizePayload({ automationThreshold: data.automationThreshold });
  }
}
